import { ReactElement, ReactNode, useEffect, useMemo, useRef } from 'react';

import styled from 'styled-components';

import {
  ActionCard,
  ActionCardContent,
  ActionCardDescription,
  ActionCardHeader,
} from '@components/action-card';
import { AssignList } from '@components/assign-button';
import { Button } from '@components/button';
import { createForm, useFormField, useCreateFormStore } from '@components/form';
import { FormDataType, FormInstance } from '@components/form/create-form';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton, IconButtonProps } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconApplicants } from '@components/icons/applicants';
import { IconAssign } from '@components/icons/assign';
import { IconCompany } from '@components/icons/company';
import { IconCopy } from '@components/icons/copy';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconPersonDetails } from '@components/icons/icon-person-details';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconPerson } from '@components/icons/person';
import { IconTextFormatLinkOff } from '@components/icons/text-format-link-off';
import { IconTrash } from '@components/icons/trash';
import { IconTrigger } from '@components/icons/trigger';
import { Menu } from '@components/menu';
import { hideModal } from '@components/modal';
import { forceHideModal } from '@components/modal/modal';
import {
  forceHideSidebar,
  hideSidebar,
  Sidebar,
  SimpleHeaderProps,
  useIsSidebarActive,
  useSidebarState,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { StatusIndicator, StatusIndicatorProps } from '@components/status-indicator';
import { Text } from '@components/text';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
} from '@components/text-editors/addons/field-mapping';
import { fieldMappingIconColor } from '@components/text-editors/addons/field-mapping-type-maps';
import { TextAreaDynamic } from '@components/textarea/textarea-dynamic';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { XOR } from '@utils/types';

import { useCompanyTravellerAttributeTemplateVariables } from 'api/attributes/hooks';
import {
  useDuplicateJourneyAction,
  useJourneyActionDependants,
  useRemoveJourneyAction,
} from 'api/journey-actions/hooks';
import { useJourney } from 'api/journeys/hooks';
import { ActionVariable, BASE_ACTION_TYPE, JourneyAction } from 'api/types';
import { ActionIcon } from 'ui/actions/action-icon';
import { actionLabels } from 'ui/actions/action-label';
import { ActionType } from 'ui/actions/calendar-event/common';
import { useNodes as useTravellerNodes } from 'ui/traveller-journey-builder/hooks/use-nodes';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import { JourneyNode } from './types';

import { useNodes } from '../hooks/use-nodes';
import { MAX_VISIBLE_PRESENCE_AVATARS } from '../presence/constants';
import { usePresence } from '../presence/presence-context';
import { UserAvatarList } from '../presence/user-avatar-list';

const AssigneesContainer = styled.div({ display: 'flex' });

type AssigneesProps = Partial<StatusIndicatorProps> & {
  assignees: JourneyNode['data']['assignees'];
};

export const Assignees = ({
  assignees = [],
  variant = 'notStarted',
  ratio,
  icon = <IconPerson />,
  tooltip,
  ...rest
}: AssigneesProps) => {
  return (
    <AssigneesContainer {...rest}>
      <AssignList>
        <div className="assignee-list">
          {assignees.length === 0 ? (
            <AssignList.Icon icon={<IconAssign />} />
          ) : (
            assignees.map((user) => <AssignList.Avatar key={user.name} assignee={user} />)
          )}
        </div>
        <div style={{ zIndex: 1 }}>
          <StatusIndicator variant={variant} icon={icon} ratio={ratio} tooltip={tooltip} />
        </div>
      </AssignList>
    </AssigneesContainer>
  );
};

const Banners = ({ node }: { node: JourneyNode }) => (
  <>
    {!node.data?.isConfigured && <IconFormIndicatorWarning />}
    {node.data?.isAutomatic ? (
      <StatusIndicator variant="notStarted" icon={<IconTrigger />} />
    ) : (
      <Assignees assignees={node.data?.assignees} />
    )}
  </>
);

export type SidebarProps = {
  journeyActionId: string;
};

export const NodeActions = ({ node }: { node: JourneyNode }) => {
  const { t } = useScopedTranslate();
  const actionBasic = nodeToActionBasic(node);
  const handleDelete = useActionRemove(actionBasic);
  const duplicateMutation = useDuplicateJourneyAction(node.id);

  return (
    <>
      <Banners node={node} />
      <div className="node-actions">
        <Menu
          buttonProps={{
            variant: 'ghost',
            iconRight: <IconMoreVertical />,
          }}
        >
          <Menu.Item
            label={t('core.duplicate')}
            onSelect={() => duplicateMutation.mutate()}
            icon={<IconCopy />}
          />
          <Menu.Item
            label={t('core.delete')}
            variant="danger"
            onSelect={handleDelete}
            icon={<IconTrash />}
          />
        </Menu>
      </div>
    </>
  );
};

export function useRawVariableGroups({
  actionVariables = [],
  types,
}: {
  actionVariables: FieldMappingGroup[];
  types?: FieldType[];
}): FieldMappingGroup[] {
  return useMemo(() => {
    if (!types) {
      return actionVariables;
    }

    return actionVariables
      .map((variable) => {
        const filteredFields = variable.fields.filter((fieldItem) => {
          return fieldItem.type != null && types.includes(fieldItem.type);
        });

        return {
          ...variable,
          fields: filteredFields,
        };
      })
      .filter((group) => group.fields.length > 0);
  }, [actionVariables, types]);
}

const extendFieldMappingGroups = (
  fieldMappingGroups: FieldMappingGroup[],
  actionVariables: ActionVariable[]
): FieldMappingGroup[] => {
  for (const actionVariable of actionVariables) {
    const keyFieldPairs = Object.entries(actionVariable.fields);
    if (keyFieldPairs.length > 0) {
      const fields: FieldMappingFieldItem[] = keyFieldPairs.map(
        ([key, { label, type, options, variable }]) => ({
          source: 'action',
          field: key,
          label,
          type,
          variable,
          action: actionVariable.shortId,
          ...(options && { options }),
        })
      );

      const groupIcon = <ActionIcon action={actionVariable.action} color={fieldMappingIconColor} />;
      fieldMappingGroups.push({
        id: actionVariable.shortId,
        action: actionVariable.action,
        description: actionVariable.description,
        icon: groupIcon,
        label: actionVariable.label ?? actionLabels[actionVariable.action],
        fields,
      });
    }
  }

  return fieldMappingGroups;
};

export const useVariableLocale = () => useScopedTranslate(variableLocale).t;

export const useVariableGroups = ({
  actionVariables = [],
  journeyId,
  types = undefined,
  excludedTypes = ['array'],
}: {
  actionVariables: ActionVariable[] | undefined;
  journeyId: string;
  types?: FieldType[] | undefined;
  excludedTypes?: FieldType[] | undefined;
}): FieldMappingGroup[] => {
  const { t } = useScopedTranslate(variableLocale);
  const journeyQuery = useJourney(journeyId);
  const companytravellerAttributeVariablesQuery = useCompanyTravellerAttributeTemplateVariables();
  const travellerJourneyAttributeVariables = journeyQuery.data?.attributeVariables ?? [];
  const companytravellerAttributeVariables = companytravellerAttributeVariablesQuery.data ?? [];
  const travellerJourneyAttributeFiltered = travellerJourneyAttributeVariables.filter(
    (attr) => !excludedTypes.includes(attr.type as FieldType)
  );
  const baseVariableGroups = [
    {
      id: 'traveller',
      icon: <IconApplicants color={fieldMappingIconColor} />,
      label: t('traveller'),
      fields: [
        {
          label: t('core.fullName'),
          source: 'company_traveller',
          field: 'name',
          type: 'text',
        },
        {
          label: t('core.firstName'),
          source: 'company_traveller',
          field: 'first_name',
          type: 'text',
        },
        {
          label: t('core.lastName'),
          source: 'company_traveller',
          field: 'last_name',
          type: 'text',
        },
        { label: t('core.email'), source: 'traveller', field: 'email', type: 'email' },
        {
          label: t('core.phone'),
          source: 'company_traveller',
          field: 'phone',
          type: 'phone',
        },
        { label: t('travellerId'), source: 'traveller_journey', field: 'id', type: 'text' },
        { label: t('core.url'), source: 'traveller_journey', field: 'url', type: 'text' },
        ...companytravellerAttributeVariables,
        ...(travellerJourneyAttributeFiltered.length > 0 ? [{ type: 'separator' }] : []),
        ...travellerJourneyAttributeVariables,
      ],
    },
    {
      id: 'journey',
      icon: <IconJourneyItem color={fieldMappingIconColor} />,
      label: t('journey'),
      fields: [{ label: t('journeyTitle'), source: 'journey', field: 'title', type: 'text' }],
    },
    {
      id: 'company',
      icon: <IconCompany color={fieldMappingIconColor} />,
      label: t('company'),
      fields: [
        {
          label: t('companyName'),
          source: 'company',
          field: 'name',
          type: 'text',
          icon: <IconCompany color={fieldMappingIconColor} />,
        },
      ],
    },
  ] as FieldMappingGroup[];

  const baseVariableGroupsMapped = baseVariableGroups.map((group) => ({
    ...group,
    fields: group.fields.map((field) => ({
      ...field,
      icon:
        field.icon ??
        (['name', 'first_name', 'last_name'].includes(field.field) ? (
          <IconPersonDetails color={fieldMappingIconColor} />
        ) : undefined),
    })),
  }));

  return extendFieldMappingGroups(baseVariableGroupsMapped, actionVariables)
    .map((group) => ({
      ...group,
      fields: group.fields
        .filter((field) => {
          if (!field.type || field.type === 'separator') {
            return true;
          }
          const isExcludedType = excludedTypes.includes(field.type);
          const isExplicitlyIncluded = types?.includes(field.type);

          return types
            ? (!isExcludedType || isExplicitlyIncluded) && types.includes(field.type)
            : !isExcludedType;
        })
        .filter((field, index, array) => {
          return field.type !== 'separator' || (index !== 0 && index !== array.length - 1);
        }),
    }))
    .filter((group) => group.fields.length > 0);
};

type DescriptionFormFieldProps = {
  isTraveller?: boolean;
  defaultValue?: string;
  textarea?: boolean;
  getFallbackNodeDescription?: () => string;
};

export const DescriptionFormField = ({
  defaultValue,
  isTraveller = false,
  getFallbackNodeDescription,
}: DescriptionFormFieldProps) => {
  const { t } = useScopedTranslate(variableLocale);
  const useNodesHook = isTraveller ? useTravellerNodes : useNodes;
  const { updateSelectedNodeData } = useNodesHook();
  const initalValue = useRef(defaultValue ?? '');

  const { value, ...formProps } = useFormField({
    id: 'description',
    label: t('description'),
    validators: [],
    defaultValue: initalValue.current,
  });

  return (
    <TextAreaDynamic
      {...formProps}
      data-cy="description-field"
      defaultValue={initalValue.current}
      placeholder={t('addDescription')}
      maxLength={1000}
      onChange={(description) => {
        if (!Boolean(description.trim()) && getFallbackNodeDescription) {
          updateSelectedNodeData?.({
            description: '',
            nodeDescription: getFallbackNodeDescription(),
          });
        } else {
          updateSelectedNodeData?.({ description, nodeDescription: description });
        }
        formProps.onChange(description);
      }}
    />
  );
};

type ActionRemoveButtonProps = Pick<IconButtonProps, 'disabled' | 'tooltip'> & {
  action: JourneyActionBasic;
};

export const nodeToActionBasic = (node: JourneyNode | TravellerJourneyNode): ActionBasic => ({
  action: node.type as ActionType,
  shortId: node.id,
  description: node.data?.description,
});

export const ActionRemoveButton = ({ action, ...rest }: ActionRemoveButtonProps) => {
  const showDeleteModal = useActionRemove(action);

  return (
    <IconButton
      icon={<IconTrash />}
      variant="ghost-purple"
      stopPropagation
      onClick={showDeleteModal}
      {...rest}
    />
  );
};

export const useActionRemove = (action: JourneyActionBasic) => {
  const { t } = useScopedTranslate(variableLocale);
  const { getValidNodes } = useNodes();
  const showDeleteModalRegular = useDeleteModal();
  const showDeleteModalLarge = useDeleteModal('LARGE');
  const dependantsQuery = useJourneyActionDependants(action.shortId, { enabled: false });
  const removeMutation = useRemoveJourneyAction();
  const isInCurrentSidebar = useIsSidebarActive(action.shortId);

  const handleRemove = async () => {
    await removeMutation.mutateAsync(action.shortId);
    if (isInCurrentSidebar) {
      requestAnimationFrame(() => {
        forceHideModal();
        forceHideSidebar();
      });
    }
  };

  const handleTrigger = async () => {
    const { data: dependants } = await dependantsQuery.refetch();
    const dependantsCount = dependants?.length ?? 0;
    const showDeleteModal = dependantsCount > 0 ? showDeleteModalLarge : showDeleteModalRegular;
    const modalDescription =
      dependantsCount === 0 ? t('confirmDescriptionEmpty') : t('confirmDescription');

    const nodes = getValidNodes();
    const confirmationNodes = dependants?.map(([parentNodeId, childNodeId]) => {
      const parentNode = nodes.find((node) => node.id === parentNodeId);
      const childNode = nodes.find((node) => node.id === childNodeId);
      return [parentNode, childNode];
    }) as [JourneyNode<BASE_ACTION_TYPE>, JourneyNode<BASE_ACTION_TYPE>][];

    showDeleteModal({
      description: modalDescription,
      onConfirm: handleRemove,
      children:
        dependantsCount === 0 ? null : (
          <Stack space={2}>
            <Text font="semibold14">{t('confirmActionLabel')}</Text>
            {confirmationNodes?.map(([parentNode, childNode]) => (
              <Stack
                key={`${parentNode.id}-${childNode.id}`}
                orientation="horizontal"
                justify="space-between"
                align="center"
                space={1}
              >
                <GenericActionCard action={nodeToActionBasic(parentNode)} />
                <IconTextFormatLinkOff color="elementRed100" />
                <GenericActionCard action={nodeToActionBasic(childNode)} />
              </Stack>
            ))}
          </Stack>
        ),
    });
  };

  return handleTrigger;
};

type ActionDuplicateButtonProps = {
  journeyActionShortId: string;
};

export const ActionDuplicateButton = ({ journeyActionShortId }: ActionDuplicateButtonProps) => {
  const duplicateMutation = useDuplicateJourneyAction(journeyActionShortId);

  return (
    <IconButton
      variant="ghost-purple"
      icon={<IconCopy />}
      onClick={() => duplicateMutation.mutate()}
      disabled={duplicateMutation.isLoading}
    />
  );
};

type ActionHeaderProps = SimpleHeaderProps & {
  customIcon?: ReactElement<IconProps>;
  journeyActionId?: string;
};

const SidebarPresenceIndicator = ({ actionId }: { actionId: string }) => {
  const { actionEditors, activeUsers, currentUserId } = usePresence();

  const editorUserIds = actionEditors.get(actionId);
  const editors = editorUserIds
    ? activeUsers.filter((user) => editorUserIds.has(user.userId) && user.userId !== currentUserId)
    : [];

  return (
    <UserAvatarList
      users={editors}
      maxVisible={MAX_VISIBLE_PRESENCE_AVATARS}
      tooltipPlacement="bottom"
    />
  );
};

export const ActionHeader: React.FC<ActionHeaderProps> = ({
  children,
  customIcon,
  journeyActionId,
  ...rest
}) => {
  const { activeId } = useSidebarState();
  const { sendActionOpened, sendActionClosed } = usePresence();

  useEffect(() => {
    if (journeyActionId && activeId === journeyActionId) {
      sendActionOpened(journeyActionId);
      return () => {
        sendActionClosed(journeyActionId);
      };
    }
  }, [journeyActionId, activeId, sendActionOpened, sendActionClosed]);

  return (
    <Sidebar.SimpleHeader {...rest} customIcon={customIcon} onClose={hideSidebar}>
      {journeyActionId && activeId === journeyActionId && (
        <SidebarPresenceIndicator actionId={journeyActionId} />
      )}
      {children}
    </Sidebar.SimpleHeader>
  );
};

type ActionFooterProps<T> = {
  action: JourneyActionBasic & { isConfigured: boolean };
  isDisabled: boolean;
  form: FormInstance<T>;
  onPreview?: () => void;
  onCancel?: () => void;
  hideActionButtons?: boolean;
  children?: ReactNode;
};

export const ActionFooter = <T extends FormDataType>({
  action,
  isDisabled,
  form: Form,
  onPreview,
  onCancel,
  hideActionButtons,
  children,
}: ActionFooterProps<T>) => {
  const { t } = useScopedTranslate(variableLocale);

  return (
    <Sidebar.Footer>
      <Stack orientation="horizontal" justify="space-between">
        <Stack align="center" space={2} orientation="horizontal">
          {!hideActionButtons && (
            <>
              <ActionRemoveButton action={action} />
              <ActionDuplicateButton journeyActionShortId={action.shortId} />
            </>
          )}
          {onPreview && action.isConfigured && (
            <IconButton variant="ghost-purple" icon={<IconOpenPreview />} onClick={onPreview} />
          )}
          {children}
        </Stack>
        <Stack orientation="horizontal" space={2}>
          <Button variant="secondary" text={t('core.cancel')} onClick={onCancel ?? hideSidebar} />
          <Form.Submit
            data-cy="action-save-btn"
            variant="primary-blue"
            text={t('core.save')}
            isDisabled={isDisabled}
          />
        </Stack>
      </Stack>
    </Sidebar.Footer>
  );
};

const variableLocale = {
  en: {
    traveller: 'Traveller',
    travellerId: 'Journey traveller ID',
    journey: 'Journey details',
    journeyTitle: 'Journey title',
    company: 'Company',
    companyName: 'Company name',
    description: 'Description',
    addDescription: 'Add description',
    confirmDescriptionEmpty: 'Are you sure you want to delete this action?',
    confirmDescription:
      'Moving this action will break connected values between the actions below.\n\nAre you sure you want to delete this action?',
    confirmActionLabel: 'Connected actions',
  },
};

export const GenericActionCard = ({ action }: { action: ActionBasic }) => {
  return (
    <NodeActionCardContainer>
      <ActionCard skipHoverState>
        <ActionCardHeader
          icon={<ActionIcon action={action.action} />}
          label={actionLabels[action.action]}
        />
        <ActionCardContent>
          <ActionCardDescription text={action.description ?? ''} />
        </ActionCardContent>
      </ActionCard>
    </NodeActionCardContainer>
  );
};

export type ActionBasic = {
  action: BASE_ACTION_TYPE;
  description?: string;
  shortId: string;
};

export type JourneyActionBasic = Pick<JourneyAction, 'action' | 'shortId' | 'description'>;

export const NodeActionCardContainer = styled.div({
  width: '100%',
  '> div': {
    width: '100% !important',
  },
});

export type NodeProp = {
  node: JourneyNode<BASE_ACTION_TYPE> | TravellerJourneyNode<BASE_ACTION_TYPE>;
};

export type JourneyIconButtonProps = XOR<NodeProp, { icon: ReactElement<IconProps> }> & {
  color: Color;
  isSelectable?: boolean;
  cursor?: 'pointer' | 'default';
  onClick?: () => void;
};

export const JourneyIconButton = ({
  node,
  icon,
  color,
  isSelectable = false,
  cursor,
  onClick,
}: JourneyIconButtonProps) => {
  const isActive = useIsSidebarActive(node?.id ?? '');
  const finalIcon = icon ?? <ActionIcon action={node?.type} color={color} />;

  return (
    <StyledJourneyIconButton
      $isActive={isActive}
      $isSelectable={isSelectable}
      $cursor={cursor}
      onClick={onClick}
    >
      {finalIcon}
    </StyledJourneyIconButton>
  );
};

export const StyledJourneyIconButton = styled.div<{
  $isActive?: boolean;
  $isSelectable: boolean;
  $cursor?: 'pointer' | 'default';
}>(({ $isActive, $isSelectable, $cursor }) => ({
  transition: 'all 0.2s ease-out',
  alignItems: 'center',
  backgroundColor: theme.color.backgroundLightDark,
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  borderColor: theme.color.border20,
  borderRadius: pxToRem(8),
  display: 'flex',
  height: pxToRem(64),
  justifyContent: 'center',
  width: pxToRem(64),
  cursor: $cursor,

  ...($isSelectable && {
    boxShadow: theme.shadow.focus,
    '--shadow-focus-width': pxToRem(8),
    '--shadow-focus-gap': 0,

    ':hover': {
      ...(!$isActive && {
        '--shadow-focus-color': theme.color.uiTrans10,
      }),
    },
  }),

  ...($isActive && {
    borderWidth: pxToRem(2),
    borderColor: theme.color.elementBlue100,
    '--shadow-focus-color': theme.color.blueTrans20,
  }),
}));

export const createActionForm = <T = any,>() =>
  createForm<T>({ onSubmitSuccess: hideModal, validationMode: 'onSubmit' });

export const useCreateActionFormStore = (
  initialState?: Parameters<typeof useCreateFormStore>[0]
) => {
  return useCreateFormStore({ validationMode: 'onSubmit', ...initialState });
};
